import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  Dispatch,
  SetStateAction,
  ReactNode,
} from "react";
import { ProfileSettings, RoomStates, RoomStatus } from "../Types/Game";
import useEscapeRoomApi from "../Hooks/useEscapeRoomApi";
import { defaultTimeout, initialProfileSettings } from "../Config/config";
import { generateRandomRoomVariant, nextVariantNoRepeat } from "../Helpers/helpers";

export interface IGameContext {
  isWinnerAccessible: boolean;
  setIsWinnerAccessible: Dispatch<SetStateAction<boolean>>;
  teamName?: string;
  setTeamName: Dispatch<SetStateAction<string | undefined>>;
  currentRoom: string | undefined;
  setCurrentRoom: Dispatch<SetStateAction<string | undefined>>;
  isTimeUp: boolean;
  setIsTimeUp: Dispatch<SetStateAction<boolean>>;
  startTime: number;
  setStartTime: Dispatch<SetStateAction<number>>;
  resetGame: () => void;
  resetRoomProgress: () => void;
  currentRoomVariant: 0 | 1 | 2;
  setCurrentRoomVariant: Dispatch<SetStateAction<0 | 1 | 2>>;
  roomStates: RoomStates | undefined;
  hasLoadedRoomStates: boolean;
  profileSettings: ProfileSettings | undefined;
  setProfileSettings: Dispatch<SetStateAction<ProfileSettings | undefined>>;
  updateEventName: (name: string) => void;
  eventName?: string;
  schoolName?: string;
  setSchoolName: Dispatch<SetStateAction<string | undefined>>;
  progressGame: () => void;
  singleRoom: boolean;
  setSingleRoom: Dispatch<SetStateAction<boolean>>;
  applyProfile: (profile: ProfileSettings) => void;
}

type Props = { children: ReactNode };

// --- helpers -------------------------------------------------------

/** Loosely-typed room state as it may appear in legacy/unvalidated data (e.g. localStorage). */
type LegacyRoomState = {
  order?: unknown;
  active?: unknown;
  status?: unknown;
  completed?: unknown;
};

/** Coerce room states to have numeric `order`, boolean `active`, and valid `status`. */
const normalizeRooms = (rooms?: Record<string, LegacyRoomState>): RoomStates => {
  const src = rooms ?? {};
  const out: RoomStates = {};
  Object.entries(src).forEach(([key, state]) => {
    if (!state) return;

    // order number
    const rawOrder = state.order;
    const order = typeof rawOrder === "number" ? rawOrder : Number(rawOrder);
    const numOrder = Number.isFinite(order) ? order : 9999;

    // active boolean
    const rawActive = state.active;
    const active =
      typeof rawActive === "boolean" ? rawActive : String(rawActive).toLowerCase() === "true";

    // status RoomStatus (preserve if present; default from legacy flags)
    let status = state.status as RoomStatus | undefined;
    if (!status) {
      status = state.completed ? RoomStatus.Completed : RoomStatus.Open;
    }

    out[key] = {
      ...state,
      order: numOrder,
      active,
      status,
    };
  });
  return out;
};

export const GameContext = createContext<IGameContext>({
  isWinnerAccessible: false,
  setIsWinnerAccessible: () => {},
  teamName: undefined,
  setTeamName: () => {},
  currentRoom: "",
  setCurrentRoom: () => {},
  isTimeUp: false,
  setIsTimeUp: () => {},
  startTime: 0,
  setStartTime: () => {},
  resetGame: () => {},
  resetRoomProgress: () => {},
  currentRoomVariant: 0,
  setCurrentRoomVariant: () => {},
  roomStates: {},
  hasLoadedRoomStates: false,
  profileSettings: {
    profileName: "",
    escapeRoomTime: 0,
    rooms: {},
    schoolInput: false,
    singleRoom: false,
    showDifficultyIndicators: true,
    branding: "gemba",
  },
  setProfileSettings: () => {},
  eventName: undefined,
  updateEventName: () => {},
  schoolName: undefined,
  setSchoolName: () => {},
  progressGame: () => {},
  singleRoom: false,
  setSingleRoom: () => {},
  applyProfile: () => {},
});

export const GameProvider = ({ children }: Props) => {
  const [isWinnerAccessible, setIsWinnerAccessible] = useState(false);
  const [teamName, setTeamName] = useState<string | undefined>(undefined);
  const [schoolName, setSchoolName] = useState<string | undefined>(undefined);
  const [eventName, setEventName] = useState<string | undefined>(undefined);
  const [isTimeUp, setIsTimeUp] = useState<boolean>(false);
  const [startTime, setStartTime] = useState<number>(0);
  const [currentRoomVariant, setCurrentRoomVariant] = useState<0 | 1 | 2>(() =>
    generateRandomRoomVariant()
  );

  const [roomStates, setRoomStates] = useState<RoomStates | undefined>();
  const [hasLoadedRoomStates, setHasLoadedRoomStates] = useState(false);
  const [singleRoom, setSingleRoom] = useState<boolean>(false);

  const { getProfileSettings } = useEscapeRoomApi();
  const [profileSettings, setProfileSettings] = useState<ProfileSettings | undefined>();

  // whenever profileSettings changes, normalise + push into state + persist
  useEffect(() => {
    if (!profileSettings) return;

    const normalizedRooms = normalizeRooms(profileSettings.rooms);
    // Sort by order for stable UI (optional)
    const sorted = Object.fromEntries(
      Object.entries(normalizedRooms).sort(([, a], [, b]) => a.order - b.order)
    );

    setRoomStates(sorted);
    setSingleRoom(Boolean(profileSettings.singleRoom));
    setHasLoadedRoomStates(true);

    // persist a normalised shape
    const toPersist: ProfileSettings = { ...profileSettings, rooms: sorted };
    localStorage.setItem("profile", JSON.stringify(toPersist));
  }, [profileSettings]);

  // ---- fetching / event binding ----------------------------------

  const getRoomStatesFromAPI = async (name: string): Promise<ProfileSettings> => {
    const settings: ProfileSettings = await getProfileSettings(name);
    // Let the profileSettings effect normalise/persist, just return raw here
    return settings;
  };

  const getRoomStatesWithTimeout = async (name: string, timeoutMs: number): Promise<ProfileSettings> => {
    const timeoutPromise = new Promise<ProfileSettings>((resolve) =>
      setTimeout(() => resolve(initialProfileSettings), timeoutMs)
    );
    try {
      const apiPromise = getRoomStatesFromAPI(name);
      return await Promise.race([apiPromise, timeoutPromise]);
    } catch (error) {
      console.error("API failed, loading local profile:", error);
      return initialProfileSettings;
    }
  };

  const fetchProfileSettingsForEvent = async (name: string) => {
    if (!name) return;
    try {
      const settings = await getRoomStatesWithTimeout(name, defaultTimeout);
      setProfileSettings(settings); // triggers normalisation effect
    } catch (error) {
      console.error("Failed to fetch profile settings for event:", name, error);
    }
  };

  const [fetchTrigger, setFetchTrigger] = useState(false);

  const updateEventName = (name: string) => {
    setHasLoadedRoomStates(false);
    setEventName(name);
    setFetchTrigger(true); // bump trigger every call
  };

  useEffect(() => {
    if (eventName && fetchTrigger) {
      resetRoomProgress(false);
      fetchProfileSettingsForEvent(eventName);
      localStorage.setItem("Event", eventName);
      setFetchTrigger(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTrigger, eventName]); // resetRoomProgress and fetchProfileSettingsForEvent are stable

  // ---- initial bootstrap from localStorage -----------------------

  useEffect(() => {
    const loadRoomStates = async () => {
      const saved = localStorage.getItem("profile");
      if (saved) {
        const parsed = JSON.parse(saved);

        // Validate & normalise from storage
        const validated: ProfileSettings = {
          profileName: parsed.profileName,
          escapeRoomTime: Number(parsed.escapeRoomTime), // numeric if possible
          schoolInput: Boolean(parsed.schoolInput),
          singleRoom: Boolean(parsed.singleRoom),
          // Default true for profiles saved before this setting existed.
          showDifficultyIndicators: parsed.showDifficultyIndicators !== false,
          branding: parsed.branding,
          rooms: normalizeRooms(parsed.rooms),
        };

        setProfileSettings(validated); // will set roomStates/singleRoom via effect
        setEventName(localStorage.getItem("Event") ?? undefined);
      } else {
        setHasLoadedRoomStates(true);
      }
    };

    loadRoomStates();
  }, []);

  // ---- gameplay helpers ------------------------------------------

  const getFirstIncompleteKey = (states: RoomStates | undefined) => {
    if (!states) return "room1";
    const first = Object.entries(states).find(
      ([, value]) => value.active && value.status === RoomStatus.Open
    );
    return first ? first[0] : undefined;
  };

  const [currentRoom, setCurrentRoom] = useState<string | undefined>();

  const progressGame = () => {
    setStartTime(Math.floor(new Date().getTime() / 1000));
    setCurrentRoomVariant((prev) => nextVariantNoRepeat(prev));
    if (!currentRoom) {
      const firstIncomplete = getFirstIncompleteKey(roomStates);
      if (firstIncomplete && firstIncomplete !== currentRoom) {
        setCurrentRoom(firstIncomplete);
      }
    }
  };

  // reset game configs not progress
  const resetGame = () => {
    setIsWinnerAccessible(false);
    if (!profileSettings?.singleRoom) {
      setTeamName(undefined);
      setSchoolName(undefined);
    }
    setCurrentRoom(getFirstIncompleteKey(roomStates));
    setIsTimeUp(false);
    setStartTime(0);
  };

  const resetRoomProgress = (loadStates = true) => {
    if (!roomStates) return;
    if (loadStates) {
      const newRoomStates = Object.fromEntries(
        Object.entries(roomStates).map(([key, state]) => [
          key,
          { ...state, status: RoomStatus.Open },
        ])
      );
      setProfileSettings((prev) => (prev ? { ...prev, rooms: newRoomStates } : prev));
    }
    setIsWinnerAccessible(false);
    setTeamName(undefined);
    setSchoolName(undefined);
    setCurrentRoom(undefined);
    setIsTimeUp(false);
    setStartTime(0);
  };

  // allow Settings to push a freshly fetched/edited profile straight into the game
  const applyProfile = useCallback((profile: ProfileSettings) => {
    // rely on setProfileSettings effect to normalise, set roomStates, singleRoom, persist, etc.
    setProfileSettings(profile);
  }, []);

  return (
    <GameContext.Provider
      value={{
        isWinnerAccessible,
        setIsWinnerAccessible,
        teamName,
        setTeamName,
        currentRoom,
        setCurrentRoom,
        isTimeUp,
        setIsTimeUp,
        startTime,
        setStartTime,
        resetGame,
        resetRoomProgress,
        currentRoomVariant,
        setCurrentRoomVariant,
        roomStates,
        hasLoadedRoomStates,
        profileSettings,
        setProfileSettings,
        eventName,
        updateEventName,
        schoolName,
        setSchoolName,
        progressGame,
        singleRoom,
        setSingleRoom,
        applyProfile,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};
